import { Preferences } from '@capacitor/preferences'

export const THEMES = [
  { id: "light", label: "Claro", icon: "i-heroicons-sun" },
  { id: "dark", label: "Escuro", icon: "i-heroicons-moon" },
  { id: "ocean", label: "Oceano", icon: "i-heroicons-globe-alt" },
  { id: "sunset", label: "Pôr do Sol", icon: "i-heroicons-sun" },
  { id: "glass", label: "Glass", icon: "i-heroicons-sparkles" },
] as const;

export type ThemeId = (typeof THEMES)[number]["id"];

// Bump este número sempre que mudar estrutura de variáveis CSS de tema.
// Garante que browsers com localStorage antigo não quebrem.
export const THEME_VERSION = "2";

const DARK_THEMES: ThemeId[] = ["dark", "glass"];
const STORAGE_KEY = "clinica-theme";
const STORAGE_VERSION_KEY = "clinica-theme-v";

async function persistTheme(theme: ThemeId) {
  localStorage.setItem(STORAGE_KEY, theme);
  localStorage.setItem(STORAGE_VERSION_KEY, THEME_VERSION);
  try {
    await Preferences.set({ key: STORAGE_KEY, value: theme })
  } catch {}
}

function clearThemeStorage() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_VERSION_KEY);
  localStorage.removeItem("nuxt-color-mode");
  localStorage.removeItem("clinica-glow");
}

async function readTheme(): Promise<ThemeId | null> {
  const version = localStorage.getItem(STORAGE_VERSION_KEY);
  if (version !== THEME_VERSION) {
    clearThemeStorage();
    return null;
  }
  const ls = localStorage.getItem(STORAGE_KEY) as ThemeId | null
  if (ls && THEMES.some((t) => t.id === ls)) return ls
  try {
    const { value } = await Preferences.get({ key: STORAGE_KEY })
    if (value && THEMES.some((t) => t.id === value)) return value as ThemeId
  } catch {}
  return null
}

export const useTheme = () => {
  const colorMode = useColorMode();

  const currentTheme = useState<ThemeId>("clinica-theme", () => "light");

  function applyTheme(theme: ThemeId) {
    currentTheme.value = theme;
    const dark = DARK_THEMES.includes(theme);
    if (import.meta.client) {
      persistTheme(theme);
      localStorage.setItem("nuxt-color-mode", dark ? "dark" : "light");
      document.documentElement.setAttribute("data-theme", theme);
      if (dark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
    colorMode.preference = dark ? "dark" : "light";
  }

  async function initTheme() {
    if (!import.meta.client) return;
    const stored = await readTheme()
    applyTheme(stored ?? "light");
  }

  const setTheme = (theme: ThemeId) => {
    applyTheme(theme);
  };

  const toggleTheme = () => {
    const ids: ThemeId[] = ["light", "dark", "ocean", "sunset", "glass"];
    const idx = ids.indexOf(currentTheme.value);
    setTheme(ids[(idx + 1) % ids.length]);
  };

  const isDark = computed(() => DARK_THEMES.includes(currentTheme.value));
  const isLight = computed(() => !DARK_THEMES.includes(currentTheme.value));
  const isCurrent = (theme: ThemeId) => currentTheme.value === theme;

  return {
    currentTheme,
    setTheme,
    toggleTheme,
    initTheme,
    isDark,
    isLight,
    isCurrent,
  };
};
